import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare, Loader2 } from "lucide-react";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import LucideIconByName from "@/components/LucideIconByName";

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
}

export default function ForumsPage() {
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/forums").then(({ data }) => setForums(data || []))
      .catch(() => setForums([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--tt-bg)] text-[var(--tt-text)]">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 min-w-0">
          <Header />
          <main className="px-4 md:px-8 py-6 max-w-6xl">
            <div className="flex items-center gap-3 mb-4">
              <MessagesSquare className="w-5 h-5 text-[var(--tt-primary)]" />
              <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">Forums</h1>
            </div>

            {loading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-[var(--tt-text-muted)]">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : forums.length === 0 ? (
              <div className="text-center text-sm text-[var(--tt-text-muted)] py-16 border border-dashed border-[var(--tt-border)] rounded">
                No forums yet. Admins can create them from the dashboard.
              </div>
            ) : (
              <div data-testid="forums-list" className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded overflow-hidden">
                {/* Section header bar */}
                <div className="px-5 py-3 bg-[var(--tt-surface-2)] border-b border-[var(--tt-border)]">
                  <h2 className="font-display font-bold text-base tracking-tight">Forums Related</h2>
                </div>

                {/* Rows */}
                <ul>
                  {forums.map((f, idx) => (
                    <li
                      key={f.forum_id}
                      className={idx > 0 ? "border-t border-[var(--tt-border)]" : ""}
                    >
                      <Link
                        to={`/forums/${f.slug}`}
                        data-testid={`forum-card-${f.slug}`}
                        className="flex items-center gap-5 px-5 py-4 hover:bg-white/[0.02] transition-colors"
                      >
                        {/* Icon tile */}
                        <div
                          className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0"
                          style={{
                            background: f.color,
                            boxShadow: `0 2px 12px ${f.color}44`,
                          }}
                        >
                          <LucideIconByName name={f.icon} className="w-7 h-7" color="#fff" strokeWidth={2.2} />
                        </div>

                        {/* Title + description */}
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display font-bold text-base text-[var(--tt-primary)] hover:underline">
                            {f.name}
                          </h3>
                          {f.description && (
                            <p className="text-xs text-[var(--tt-text-muted)] mt-1 leading-relaxed">
                              {f.description}
                            </p>
                          )}
                        </div>

                        {/* Post-count column */}
                        <div className="hidden sm:flex flex-col items-center w-20 shrink-0 text-center">
                          <div className="font-display text-lg font-extrabold">{f.post_count}</div>
                          <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mt-0.5">Posts</div>
                        </div>

                        {/* Last-post column */}
                        <div className="hidden md:flex items-center gap-3 w-64 shrink-0">
                          {f.last_post_at ? (
                            <>
                              <div className="text-xs leading-tight min-w-0 text-right ml-auto">
                                <div className="text-[var(--tt-primary)] font-semibold truncate">Latest activity</div>
                                <div className="text-[var(--tt-text-muted)] mt-0.5">{timeAgo(f.last_post_at)}</div>
                              </div>
                            </>
                          ) : (
                            <div className="text-xs text-[var(--tt-text-faint)] italic ml-auto">No posts yet</div>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
